import { Tabs } from 'antd'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import HeaderScreen from 'src/components/shared/header-screen/HeaderScreen'
import FadeIn from 'src/components/styled/FadeIn'
import {
  BRIDGE_MINI_TABS,
  BRIDGE_MINI_TAB_CATCH_BOARD,
  BRIDGE_MINI_TAB_CROWS_NEST,
  BRIDGE_MINI_TAB_HARBOUR,
  BRIDGE_MINI_TAB_SPARTACUS,
  BRIDGE_PAGE_SUBTITLE,
  BRIDGE_PAGE_TITLE,
  buildBridgeAdminPath,
  getBridgeMiniTabFromSearch,
  getCatchBoardDealIdFromSearch,
  getHarbourVenueIdFromSearch,
  resolveBridgeMiniTab,
} from 'src/constants/bridge'
import CatchBoard from 'src/screens/catch-board/CatchBoard'
import Harbour from 'src/screens/harbour/Harbour'
import Spartacus from 'src/screens/spartacus/Spartacus'
import {
  buildCatchBoardDealFormFromCrowsNestOpportunity,
  buildCatchBoardDealFormFromSpartacusVenue,
  warnCatchBoardHandoffDuplicateVenue,
} from 'src/utils/catchBoardHandoff'
import styled from 'styled-components/macro'
import CrowsNest from './CrowsNest'

/**
 * The Bridge — admin sales home shell.
 * Mini tab 1: Crow’s Nest (Growth metrics)
 * Mini tab 2: Catch Board (sales kanban)
 * Mini tab 3: Spartacus (venue hunter) — nested; behaviour unchanged.
 * Mini tab 4: Harbour (CS work queue).
 */
const TheBridge = ({ initialTab } = {}) => {
  const location = useLocation()
  const history = useHistory()
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab != null) {
      return resolveBridgeMiniTab(initialTab)
    }
    return getBridgeMiniTabFromSearch(location.search)
  })
  const [catchBoardHandoff, setCatchBoardHandoff] = useState(null)
  const [spartacusSearchHandoff, setSpartacusSearchHandoff] = useState(null)

  const writeBridgeUrl = useCallback(
    (tab, dealId = null, venueId = null) => {
      const next = buildBridgeAdminPath(tab, { dealId, venueId })
      const current = `${history.location.pathname}${history.location.search}`
      if (current !== next) {
        history.replace(next)
      }
    },
    [history],
  )

  useEffect(() => {
    const tab = getBridgeMiniTabFromSearch(location.search)
    const dealId = getCatchBoardDealIdFromSearch(location.search)
    const venueId = getHarbourVenueIdFromSearch(location.search)
    setActiveTab(tab)
    writeBridgeUrl(tab, dealId, venueId)
  }, [location.search, writeBridgeUrl])

  const handleTabChange = useCallback(
    key => {
      const tab = resolveBridgeMiniTab(key)
      setActiveTab(tab)
      writeBridgeUrl(tab, null, null)
    },
    [writeBridgeUrl],
  )

  const handleDeepLinkDealChange = useCallback(
    dealId => {
      writeBridgeUrl(BRIDGE_MINI_TAB_CATCH_BOARD, dealId || null, null)
    },
    [writeBridgeUrl],
  )

  const handleDeepLinkVenueChange = useCallback(
    venueId => {
      writeBridgeUrl(BRIDGE_MINI_TAB_HARBOUR, null, venueId || null)
    },
    [writeBridgeUrl],
  )

  const handleAddToCatchBoard = useCallback(
    async venue => {
      // Await warn-only check so the toast is visible before we leave Spartacus.
      await warnCatchBoardHandoffDuplicateVenue({
        venueId: venue?.venueId,
        venueName: venue?.name || venue?.venueName,
      })
      setCatchBoardHandoff({
        draft: buildCatchBoardDealFormFromSpartacusVenue(venue),
        nonce: Date.now(),
      })
      setActiveTab(BRIDGE_MINI_TAB_CATCH_BOARD)
      writeBridgeUrl(BRIDGE_MINI_TAB_CATCH_BOARD, null)
    },
    [writeBridgeUrl],
  )

  const handleCreateDealFromOpportunity = useCallback(
    async opportunity => {
      await warnCatchBoardHandoffDuplicateVenue({
        venueId: opportunity?.venueId,
        venueName: opportunity?.venueName,
      })
      setCatchBoardHandoff({
        draft: buildCatchBoardDealFormFromCrowsNestOpportunity(opportunity),
        nonce: Date.now(),
      })
      setActiveTab(BRIDGE_MINI_TAB_CATCH_BOARD)
      writeBridgeUrl(BRIDGE_MINI_TAB_CATCH_BOARD, null)
    },
    [writeBridgeUrl],
  )

  const handleCreateHandoffConsumed = useCallback(() => {
    setCatchBoardHandoff(null)
  }, [])

  const handleOpenSpartacus = useCallback(
    (query = '') => {
      setSpartacusSearchHandoff({
        search: String(query || '').trim(),
        nonce: Date.now(),
      })
      setActiveTab(BRIDGE_MINI_TAB_SPARTACUS)
      // Explicit venue null: leftover ?venue= would force Harbour back
      // (venue implies Harbour) and leave the briefing on screen.
      writeBridgeUrl(BRIDGE_MINI_TAB_SPARTACUS, null, null)
    },
    [writeBridgeUrl],
  )

  const catchBoardDealId =
    activeTab === BRIDGE_MINI_TAB_CATCH_BOARD
      ? getCatchBoardDealIdFromSearch(location.search)
      : null

  const harbourVenueId =
    activeTab === BRIDGE_MINI_TAB_HARBOUR
      ? getHarbourVenueIdFromSearch(location.search)
      : null

  const tabPanes = useMemo(
    () =>
      BRIDGE_MINI_TABS.map(tab => (
        <Tabs.TabPane tab={tab.label} key={tab.key}>
          {tab.key === BRIDGE_MINI_TAB_CROWS_NEST ? (
            <CrowsNest onCreateDeal={handleCreateDealFromOpportunity} />
          ) : null}
          {tab.key === BRIDGE_MINI_TAB_CATCH_BOARD ? (
            <CatchBoard
              createHandoff={catchBoardHandoff}
              onCreateHandoffConsumed={handleCreateHandoffConsumed}
              isActive={activeTab === BRIDGE_MINI_TAB_CATCH_BOARD}
              deepLinkDealId={catchBoardDealId}
              onDeepLinkDealChange={handleDeepLinkDealChange}
            />
          ) : null}
          {tab.key === BRIDGE_MINI_TAB_SPARTACUS ? (
            <SpartacusPanel data-testid='bridge-spartacus-panel'>
              <Spartacus
                nested
                onAddToCatchBoard={handleAddToCatchBoard}
                seedSearch={spartacusSearchHandoff?.search || ''}
                searchHandoffNonce={spartacusSearchHandoff?.nonce || 0}
              />
            </SpartacusPanel>
          ) : null}
          {tab.key === BRIDGE_MINI_TAB_HARBOUR ? (
            <Harbour
              isActive={activeTab === BRIDGE_MINI_TAB_HARBOUR}
              deepLinkVenueId={harbourVenueId}
              onDeepLinkVenueChange={handleDeepLinkVenueChange}
              onOpenSpartacus={handleOpenSpartacus}
            />
          ) : null}
        </Tabs.TabPane>
      )),
    [
      activeTab,
      catchBoardDealId,
      catchBoardHandoff,
      handleAddToCatchBoard,
      handleCreateDealFromOpportunity,
      handleCreateHandoffConsumed,
      handleDeepLinkDealChange,
      handleDeepLinkVenueChange,
      handleOpenSpartacus,
      harbourVenueId,
      spartacusSearchHandoff,
    ],
  )

  return (
    <Wrapper data-testid='the-bridge'>
      <HeaderScreen title={BRIDGE_PAGE_TITLE} subTitle={BRIDGE_PAGE_SUBTITLE} />
      <StyledTabs
        activeKey={activeTab}
        onChange={handleTabChange}
        type='card'
        destroyInactiveTabPane={false}
      >
        {tabPanes}
      </StyledTabs>
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
`

const StyledTabs = styled(Tabs)`
  margin-top: ${({ theme }) => theme.spacing.md || '16px'};
  && {
    overflow: visible;
  }
`

const SpartacusPanel = styled.div`
  width: 100%;
  padding-top: ${({ theme }) => theme.spacing.sm || '8px'};
`

export default TheBridge
